import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Select, Switch } from "antd";
import { addLecture, editLecture, getLectures } from "../../redux/services/lectureSlice";
import { getWorkshops } from "../../redux/services/workshopSlice";
import { fileUpload } from "../../redux/services/fileUploadSlice";
import { ROUTES } from "../../constant";
import { AiFillFilePdf } from "react-icons/ai";

const WorkshopLectureForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const lectureId = queryParams.get("lectureId");
  const isEdit = !!lectureId;
  const lectureData = location.state?.row || {};

  const [formData, setFormData] = useState({
    workshopIds: [],
    language: "",
    subjectName: "",
    title: "",
    priority: "",
    type: "workshop", // 👈 always workshop
    subtitle: "",
    image: "",
    link: "",
    pdf: "",
    isLocked: false,
  });

  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);

  const { workshopData } = useSelector((state) => state.workshop || {});

  const validate = () => {
    const newErrors = {};
    const required = ["language", "subjectName", "title", "priority", "subtitle", "image", "link"];
    required.forEach((f) => {
      if (!formData[f]) newErrors[f] = "Required";
    });

    if (formData.workshopIds.length === 0)
      newErrors.workshopIds = "Select at least one workshop";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      ...formData,
      priority: Number(formData.priority),
    };

    try {
      const res = isEdit
        ? await dispatch(editLecture({ ...payload, lectureId }))
        : await dispatch(addLecture(payload));

      if (res?.payload?.message) toast.success(res.payload.message);
      navigate(ROUTES.WORKSHOPS.LECTURES);
      dispatch(getLectures({ page: 1, limit: 10 }));
    } catch {
      toast.error("Something went wrong");
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, workshopIds: value }));
  };

  const handleSwitchChange = (checked) => {
    setFormData((prev) => ({ ...prev, isLocked: checked }));
  };

  // const handleImageChange = async (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;

  //   setPreview(URL.createObjectURL(file));
  //   const formDataToSend = new FormData();
  //   formDataToSend.append("image", file);
  //   try {
  //     setUploading(true);
  //     const res = await dispatch(fileUpload(formDataToSend));
  //     const url = res.payload?.fileUrl || res.payload;
  //     if (url) {
  //       setFormData((prev) => ({ ...prev, image: url }));
  //       toast.success("Image uploaded successfully!");
  //     }
  //   } catch {
  //     toast.error("Image upload failed");
  //   } finally {
  //     setUploading(false);
  //   }
  // };

  const handleFileUpload = async (file, fileType) => {
    const formData = new FormData();
    if (fileType === "image") {
      formData.append("image", file);
    } else if (fileType === "pdf") {
      formData.append("pdf", file);
    }
    try {
      setUploading(true);
      const res = await dispatch(fileUpload(formData));
      const url = res.payload;
      if (url) {
        if (fileType === "image") {
          setFormData((prev) => ({ ...prev, image: url }));
        } else if (fileType === "pdf") {
          setFormData((prev) => ({ ...prev, pdf: url }));
        }
        setUploading(false);
        toast.success(`${fileType} uploaded successfully!`);
      }
    } catch {
      toast.error(`${fileType} upload failed`);
    } finally {
      setUploading(false);
    }
  }

  const handlePDFChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setPdfPreview(fileURL);
      handleFileUpload(file, "pdf");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setPreview(imageURL);
      handleFileUpload(file, "image");
    }
  };


  // Load workshops
  useEffect(() => {
    dispatch(getWorkshops({}));
  }, [dispatch]);

  // Set data if editing
  useEffect(() => {
    console.log(lectureData)
    if (isEdit && lectureData) {
      const mappedWorkshopIds = lectureData.workshopIds?.map((w) => w._id || w) || [];
      setFormData({
        workshopIds: mappedWorkshopIds,
        language: lectureData.language || "",
        subjectName: lectureData.subjectName || "",
        title: lectureData.title || "",
        priority: lectureData.priority || "",
        type: "workshop",
        subtitle: lectureData.subtitle || "",
        image: lectureData.image || "",
        link: lectureData.link || "",
        pdf: lectureData.pdf || "",
        isLocked: lectureData.isLocked ?? false,
      });
      setPreview(lectureData.image || null);
      setPdfPreview(lectureData.pdf || null);

    }
  }, [isEdit, lectureData]);

  return (
    <div className="max-w-3xl mx-auto bg-card shadow-theme rounded-xl p-8 mt-6">
      <h2 className="text-2xl font-semibold mb-6">
        {isEdit ? "Edit Workshop Lecture" : "Add Workshop Lecture"}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-5 antForm">
        {/* Workshop Select */}
        <div>
          <label className="block font-medium mb-1">Select Workshop *</label>
          <Select
            mode="multiple"
            value={formData.workshopIds}
            onChange={handleSelectChange}
            options={workshopData?.workshop_data?.map((w) => ({
              value: w._id,
              label: w.title,
            }))}
            placeholder="Select workshop(s)"
            style={{ width: "100%" }}
          />
          {errors.workshopIds && (
            <p className="text-red-500 text-sm">{errors.workshopIds}</p>
          )}
        </div>

        {/* Image Upload */}
        <div>
          <label className="block font-medium mb-1">Image *</label>
          <input
            type="file"
            onChange={handleImageChange}
            className="w-full border p-2 rounded-xl"
          />
          {errors.image && <p className="text-red-500 text-sm">{errors.image}</p>}
          {preview && (
            <div className="mt-3">
              <img
                src={preview}
                alt="preview"
                className="w-24 h-24 object-cover rounded-xl border"
              />
            </div>
          )}
        </div>

        {/* Text Inputs */}
        {[
          { name: "language", label: "Language" },
          { name: "subjectName", label: "Subject Name" },
          { name: "title", label: "Title" },
          { name: "subtitle", label: "Subtitle" },
          { name: "priority", label: "Priority" },
          { name: "link", label: "Lecture Link" },
        ].map((f) => (
          <div key={f.name}>
            <label className="block font-medium mb-1">{f.label} *</label>
            <input
              type="text"
              name={f.name}
              value={formData[f.name]}
              onChange={handleChange}
              className="w-full border p-2 rounded-xl"
            />
            {errors[f.name] && (
              <p className="text-red-500 text-sm">{errors[f.name]}</p>
            )}
          </div>
        ))}

        {/* PDF Upload */}
        <div>
          <label className="text-sm font-medium text-foreground">PDF</label>
          <input type="file" name="pdf" accept=".pdf" onChange={handlePDFChange} className="w-full px-4 py-2 border border-border rounded-xl" />
          {pdfPreview && (
            <div className="mt-4 flex items-center">
              <p className="text-md text-foreground mr-3">Preview:</p>
              <Link to={pdfPreview} target="_blank" rel="noopener noreferrer" className="flex items-center text-primary">
                <AiFillFilePdf size={40} className="mr-2" />
                View PDF
              </Link>
            </div>
          )}
        </div>

        {/* Lock Switch */}
        <div className="flex items-center gap-3">
          <label className="block font-medium mb-1">Lock Lecture</label>
          <Switch checked={formData.isLocked} onChange={handleSwitchChange} />
          <span className="text-sm text-muted-foreground">
            {formData.isLocked ? "Locked" : "Unlocked"}
          </span>
        </div>

        <button
          type="submit"
          className="bg-primary text-white px-6 py-2 rounded-xl transition"
          disabled={uploading}
        >
          {isEdit ? "Update Workshop Lecture" : uploading ? "Uploading..." : "Add Workshop Lecture"}
        </button>
      </form>
    </div>
  );
};

export default WorkshopLectureForm;
